import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Video, ArrowRight, Volume2, VolumeX } from "lucide-react";

/**
 * Bold, unmistakable event announcement banner used at the top of Home
 * and prominently on Training. Brand-consistent (navy + gold dominant,
 * tiny pink accents). Centerpiece: two golden trumpets with small gold
 * tassels that roll in from the sides when the section scrolls into view.
 *
 * Audio (short original 3-note "da-da-da" fanfare, synthesized live via
 * Web Audio API):
 *   1. On first arrival in a session, attempt autoplay. Modern browsers
 *      almost always block audio autoplay, so this generally fails silently.
 *   2. If blocked (or not attempted yet), arm a one-time global listener
 *      for the very first user gesture (pointerdown / keydown / touchstart)
 *      and play the fanfare then. This lets it feel automatic without
 *      forcing a separate "play" button.
 *   3. Never repeats within the same visit (guarded by sessionStorage).
 *   4. Never plays if the visitor has muted (guarded by localStorage).
 *   5. Small mute toggle (top-right of the banner) always available.
 *   6. prefers-reduced-motion is respected for trumpet slide animation.
 */

const FANFARE_PLAYED_KEY = "layor.fanfare.playedThisSession";
const FANFARE_MUTED_KEY = "layor.fanfare.muted";

export default function EventAnnouncement({ testid = "event-announcement", size = "hero" }) {
  const isHero = size === "hero";
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [flourish, setFlourish] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioCtxRef = useRef(null);
  const playedRef = useRef(false);

  /* Restore muted state + reduced motion */
  useEffect(() => {
    try {
      setMuted(localStorage.getItem(FANFARE_MUTED_KEY) === "1");
    } catch { /* ignore */ }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const listener = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", listener);
    return () => mq.removeEventListener?.("change", listener);
  }, []);

  /* Trumpet slide-in when banner enters viewport (audio-independent) */
  useEffect(() => {
    if (!rootRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  /* Original synthesized fanfare (Web Audio API). Returns Promise<boolean>.
     ~3 second rising brass phrase with sustained resolution. */
  const playFanfare = useCallback(async () => {
    if (playedRef.current) return true;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return false;
      const ctx = audioCtxRef.current || new AudioCtx();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch { return false; }
        if (ctx.state !== "running") return false;
      }

      // Master chain: filter (brass warmth) -> master gain -> destination.
      // Parallel: subtle short delay feedback for a hall-ish shimmer.
      const master = ctx.createGain();
      master.gain.value = 0.0001;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 3200;
      filter.Q.value = 1.1;

      const delay = ctx.createDelay(0.5);
      delay.delayTime.value = 0.12;
      const feedback = ctx.createGain();
      feedback.gain.value = 0.28;
      const wet = ctx.createGain();
      wet.gain.value = 0.18;
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(wet);

      filter.connect(master);
      filter.connect(delay);
      wet.connect(master);
      master.connect(ctx.destination);

      const now = ctx.currentTime;
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.42, now + 0.03);

      // Musical phrase in C major (Hz): pickup -> rising triad -> answering
      // triplet -> sustained C6 finish with vibrato. Total ~2.95s.
      const notes = [
        { f: 392.00, t: 0.00, d: 0.18 }, // G4 pickup
        { f: 523.25, t: 0.20, d: 0.20 }, // C5
        { f: 659.25, t: 0.42, d: 0.20 }, // E5
        { f: 783.99, t: 0.64, d: 0.52 }, // G5 (hold)
        { f: 659.25, t: 1.20, d: 0.16 }, // E5 pickup
        { f: 783.99, t: 1.38, d: 0.16 }, // G5
        { f: 1046.5, t: 1.56, d: 0.24 }, // C6
        { f: 1046.5, t: 1.82, d: 1.10, vibrato: true }, // C6 sustained finish
      ];

      notes.forEach(({ f, t, d, vibrato }) => {
        const t0 = now + t;
        const t1 = t0 + d;

        // Brass mix: fundamental (sawtooth), detuned second sawtooth (+7 cents)
        // for chorus, and square one octave up at low gain for shimmer.
        const osc1 = ctx.createOscillator();
        osc1.type = "sawtooth";
        osc1.frequency.value = f;
        const osc2 = ctx.createOscillator();
        osc2.type = "sawtooth";
        osc2.frequency.value = f;
        osc2.detune.value = 7;
        const osc3 = ctx.createOscillator();
        osc3.type = "square";
        osc3.frequency.value = f * 2;

        // Per-note envelope (attack / decay / sustain / release)
        const noteGain = ctx.createGain();
        noteGain.gain.setValueAtTime(0.0001, t0);
        noteGain.gain.exponentialRampToValueAtTime(0.34, t0 + 0.035); // attack
        noteGain.gain.exponentialRampToValueAtTime(0.24, t0 + 0.16); // decay to sustain
        // Slight down-slope during sustain for a warm, non-static tone.
        noteGain.gain.exponentialRampToValueAtTime(0.20, t1 - 0.05);
        noteGain.gain.exponentialRampToValueAtTime(0.0001, t1);

        // Mix balance
        const m1 = ctx.createGain(); m1.gain.value = 0.60;
        const m2 = ctx.createGain(); m2.gain.value = 0.40;
        const m3 = ctx.createGain(); m3.gain.value = 0.14;
        osc1.connect(m1); osc2.connect(m2); osc3.connect(m3);
        m1.connect(noteGain); m2.connect(noteGain); m3.connect(noteGain);
        noteGain.connect(filter);

        // Vibrato on sustained finish: 5 Hz sine, ~5 Hz depth on frequency,
        // fading in over 250 ms for a natural brass hold.
        if (vibrato) {
          const lfo = ctx.createOscillator();
          lfo.type = "sine";
          lfo.frequency.value = 5;
          const lfoGain = ctx.createGain();
          lfoGain.gain.setValueAtTime(0, t0);
          lfoGain.gain.linearRampToValueAtTime(5.5, t0 + 0.25);
          lfoGain.gain.linearRampToValueAtTime(3.5, t1 - 0.05);
          lfo.connect(lfoGain);
          lfoGain.connect(osc1.frequency);
          lfoGain.connect(osc2.frequency);
          lfo.start(t0);
          lfo.stop(t1 + 0.02);
        }

        osc1.start(t0); osc2.start(t0); osc3.start(t0);
        osc1.stop(t1 + 0.02); osc2.stop(t1 + 0.02); osc3.stop(t1 + 0.02);
      });

      // Clean master fade at tail
      const tail = 1.82 + 1.10 + 0.05;
      master.gain.setValueAtTime(0.42, now + tail);
      master.gain.exponentialRampToValueAtTime(0.0001, now + tail + 0.35);

      // Visual flourish paired with the audio
      setFlourish(true);
      setTimeout(() => setFlourish(false), 1600);

      playedRef.current = true;
      try { sessionStorage.setItem(FANFARE_PLAYED_KEY, "1"); } catch { /* ignore */ }
      return true;
    } catch {
      return false;
    }
  }, []);

  /* Audio orchestration: try autoplay, else arm one-time first-gesture listener */
  useEffect(() => {
    let alreadyPlayed = false;
    try {
      alreadyPlayed = sessionStorage.getItem(FANFARE_PLAYED_KEY) === "1";
    } catch { /* ignore */ }
    if (alreadyPlayed) {
      playedRef.current = true;
      return;
    }
    if (muted) return;

    let disposed = false;
    const removeArmedListeners = () => {
      document.removeEventListener("pointerdown", armedHandler, true);
      document.removeEventListener("keydown", armedHandler, true);
      document.removeEventListener("touchstart", armedHandler, true);
    };
    const armedHandler = async () => {
      if (disposed || playedRef.current) return;
      // Read live muted flag in case user toggled just before first gesture
      let liveMuted = false;
      try { liveMuted = localStorage.getItem(FANFARE_MUTED_KEY) === "1"; } catch { /* ignore */ }
      if (liveMuted) { removeArmedListeners(); return; }
      const ok = await playFanfare();
      if (ok) removeArmedListeners();
    };

    // Attempt autoplay once. Almost always blocked; we swallow the failure.
    (async () => {
      const ok = await playFanfare();
      if (!ok && !disposed) {
        document.addEventListener("pointerdown", armedHandler, true);
        document.addEventListener("keydown", armedHandler, true);
        document.addEventListener("touchstart", armedHandler, true);
      }
    })();

    return () => {
      disposed = true;
      removeArmedListeners();
    };
  }, [muted, playFanfare]);

  const toggleMuted = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(FANFARE_MUTED_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      if (next && audioCtxRef.current) {
        try { audioCtxRef.current.suspend(); } catch { /* ignore */ }
      }
      return next;
    });
  };

  return (
    <section
      ref={rootRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: "#0A152C" }}
      data-testid={testid}
      aria-label="Upcoming complimentary workshop announcement"
    >
      {/* Radial brand glows (gold-dominant, whisper of pink) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 82% 20%, rgba(198,168,90,0.34), transparent 55%), radial-gradient(circle at 14% 80%, rgba(198,168,90,0.20), transparent 55%), radial-gradient(circle at 50% 100%, rgba(227,50,120,0.08), transparent 40%)",
        }}
        aria-hidden="true"
      />
      <ConfettiDots active={flourish} reducedMotion={reducedMotion} />

      {/* Top accent strip */}
      <div className="absolute top-0 left-0 right-0 h-[3px] flex">
        <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
        <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
      </div>

      {/* Mute toggle (always visible) */}
      <button
        type="button"
        onClick={toggleMuted}
        aria-pressed={muted}
        aria-label={muted ? "Turn workshop fanfare on" : "Turn workshop fanfare off"}
        title={muted ? "Turn workshop fanfare on" : "Turn workshop fanfare off"}
        data-testid={`${testid}-mute-toggle`}
        className="absolute top-4 right-4 md:top-5 md:right-5 z-10 inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/25 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-white/85 hover:bg-black/45 backdrop-blur"
      >
        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        <span className="hidden sm:inline">{muted ? "Sound Off" : "Sound On"}</span>
      </button>

      <div className={`relative mx-auto max-w-7xl px-6 md:px-10 ${isHero ? "py-12 md:py-16" : "py-10 md:py-12"}`}>
        {/* Desktop trumpets flanking the copy */}
        <div className="hidden md:block pointer-events-none absolute inset-y-0 left-0 w-[240px] lg:w-[300px]" aria-hidden="true">
          <div
            className="absolute transition-all ease-out"
            style={{
              transitionDuration: reducedMotion ? "500ms" : "900ms",
              transform: inView
                ? "translate(-50%, -50%) rotate(-14deg) translateX(80px)"
                : "translate(-50%, -50%) rotate(-40deg) translateX(-220px)",
              opacity: inView ? 1 : 0,
              left: "50%",
              top: "50%",
            }}
          >
            <Trumpet size={isHero ? 260 : 220} />
          </div>
        </div>
        <div className="hidden md:block pointer-events-none absolute inset-y-0 right-0 w-[240px] lg:w-[300px]" aria-hidden="true">
          <div
            className="absolute transition-all ease-out"
            style={{
              transitionDuration: reducedMotion ? "500ms" : "900ms",
              transform: inView
                ? "translate(-50%, -50%) rotate(14deg) translateX(-80px) scaleX(-1)"
                : "translate(-50%, -50%) rotate(40deg) translateX(220px) scaleX(-1)",
              opacity: inView ? 1 : 0,
              left: "50%",
              top: "50%",
            }}
          >
            <Trumpet size={isHero ? 260 : 220} />
          </div>
        </div>

        {/* Mobile trumpets above copy */}
        <div className="md:hidden flex items-center justify-center gap-3 mb-6" aria-hidden="true">
          <div
            className="transition-all ease-out"
            style={{
              transitionDuration: reducedMotion ? "400ms" : "800ms",
              transform: inView ? "translateX(0) rotate(-14deg)" : "translateX(-80px) rotate(-40deg)",
              opacity: inView ? 1 : 0,
            }}
          >
            <Trumpet size={150} />
          </div>
          <div
            className="transition-all ease-out"
            style={{
              transitionDuration: reducedMotion ? "400ms" : "800ms",
              transform: inView ? "translateX(0) rotate(14deg) scaleX(-1)" : "translateX(80px) rotate(40deg) scaleX(-1)",
              opacity: inView ? 1 : 0,
            }}
          >
            <Trumpet size={150} />
          </div>
        </div>

        <div className="relative grid md:grid-cols-12 gap-6 md:gap-8 items-center">
          <div className="md:col-start-4 md:col-span-6 text-center">
            <div
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[10px] uppercase tracking-[0.32em] font-bold"
              style={{ backgroundColor: "#E6C871", color: "#0A152C" }}
            >
              <span className="relative flex h-2 w-2">
                <span
                  className="absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ backgroundColor: "#E33278", animation: reducedMotion ? "none" : "ping 1.4s cubic-bezier(0,0,0.2,1) infinite" }}
                />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: "#E33278" }} />
              </span>
              Complimentary Live Workshop
            </div>

            <h2
              className={`mt-6 font-display-black tracking-tight text-white leading-[1.02] ${
                isHero ? "text-4xl sm:text-5xl md:text-6xl lg:text-[4rem]" : "text-3xl md:text-5xl lg:text-[3rem]"
              }`}
            >
              From First Click to First Chat<span style={{ color: "#E33278" }}>.</span>
            </h2>
            <p className="mt-4 md:mt-5 text-[var(--layor-gold)] font-semibold text-xl sm:text-2xl md:text-3xl leading-snug">
              Your Guided Introduction to ChatGPT
            </p>

            <div className="mt-7 md:mt-8 flex flex-wrap gap-x-7 gap-y-3 justify-center text-white text-lg md:text-xl">
              <span className="inline-flex items-center gap-2.5">
                <CalendarDays size={22} className="text-[var(--layor-gold)]" />
                <strong className="font-semibold">Tuesday, October 27, 2026</strong>
              </span>
              <span className="inline-flex items-center gap-2.5">
                <Clock size={22} className="text-[var(--layor-gold)]" />
                <strong className="font-semibold">6:00 to 7:00 p.m. Eastern</strong>
              </span>
              <span className="inline-flex items-center gap-2.5">
                <Video size={22} className="text-[var(--layor-gold)]" />
                <strong className="font-semibold">Live via Zoom</strong>
              </span>
            </div>

            <div className="mt-9 flex justify-center">
              <Link
                to="/first-click-to-first-chat"
                data-testid={`${testid}-cta`}
                className="btn-gold inline-flex items-center gap-2 rounded-full px-9 md:px-10 py-4 md:py-5 text-base md:text-lg font-bold uppercase tracking-[0.18em] shadow-xl shadow-black/30 hover:shadow-2xl transition-all whitespace-nowrap"
              >
                Reserve Your Seat <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent strip */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
        <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
        <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
      </div>
    </section>
  );
}

/* ── Trumpet SVG (polished brass with gold tassel) ─────────────────── */
function Trumpet({ size = 220 }) {
  // Palette: warm brass with dark rim, shadow, and highlights.
  const gold = "#E9C765";
  const goldMid = "#C6A44A";
  const goldDark = "#8E6A22";
  const goldDeep = "#5C4416";
  const ivoryHi = "#FFF6D2";
  return (
    <svg
      viewBox="0 0 320 170"
      width={size}
      height={size * (170 / 320)}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.35))" }}
      aria-hidden="true"
    >
      <defs>
        {/* Brass gradient for tubing */}
        <linearGradient id="tubeGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ivoryHi} />
          <stop offset="18%" stopColor={gold} />
          <stop offset="55%" stopColor={goldMid} />
          <stop offset="100%" stopColor={goldDark} />
        </linearGradient>
        {/* Bell gradient (curved highlight to shadow) */}
        <radialGradient id="bellGrad" cx="0.35" cy="0.4" r="0.9">
          <stop offset="0%" stopColor={ivoryHi} />
          <stop offset="30%" stopColor={gold} />
          <stop offset="70%" stopColor={goldMid} />
          <stop offset="100%" stopColor={goldDark} />
        </radialGradient>
        {/* Valve casing gradient */}
        <linearGradient id="valveGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={goldDark} />
          <stop offset="15%" stopColor={gold} />
          <stop offset="55%" stopColor={ivoryHi} />
          <stop offset="85%" stopColor={goldMid} />
          <stop offset="100%" stopColor={goldDark} />
        </linearGradient>
        {/* Bell rim (thin outer band) */}
        <linearGradient id="rimGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={goldDark} />
          <stop offset="50%" stopColor={ivoryHi} />
          <stop offset="100%" stopColor={goldDeep} />
        </linearGradient>
        {/* Tassel gradient */}
        <linearGradient id="tasselGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={gold} />
          <stop offset="100%" stopColor={goldDeep} />
        </linearGradient>
      </defs>

      {/* ── Leadpipe (long straight tube from mouthpiece to first valve) */}
      <path
        d="M 22 88 L 122 84 L 122 96 L 22 100 Z"
        fill="url(#tubeGrad)"
        stroke={goldDeep}
        strokeWidth="0.7"
      />
      {/* Highlight streak on leadpipe */}
      <path d="M 26 87 L 118 84.5" stroke={ivoryHi} strokeWidth="1.1" opacity="0.85" />

      {/* ── Mouthpiece: cup + shank */}
      <path d="M 14 82 L 22 85 L 22 103 L 14 106 C 8 104, 8 84, 14 82 Z" fill="url(#tubeGrad)" stroke={goldDeep} strokeWidth="0.7" />
      <ellipse cx="10" cy="94" rx="4.2" ry="9" fill={goldDark} stroke={goldDeep} strokeWidth="0.5" />
      <ellipse cx="10" cy="94" rx="2" ry="6.5" fill="#3B2A0F" />

      {/* ── Main tuning-slide loop at the end (small U-bend before valves) */}
      <path d="M 120 82 L 138 82 C 148 82, 148 102, 138 102 L 120 102 Z" fill="none" stroke={goldMid} strokeWidth="7" strokeLinejoin="round" />
      <path d="M 120 82 L 138 82 C 148 82, 148 102, 138 102 L 120 102" fill="none" stroke={ivoryHi} strokeWidth="1.1" opacity="0.7" />

      {/* ── Return tube from tuning slide to valve block */}
      <path
        d="M 138 80 L 168 80 L 168 104 L 138 104 Z"
        fill="url(#tubeGrad)"
        stroke={goldDeep}
        strokeWidth="0.7"
      />

      {/* ── Valve casings (3 cylinders) */}
      {[
        { x: 168 },
        { x: 190 },
        { x: 212 },
      ].map(({ x }, i) => (
        <g key={i}>
          {/* Casing body */}
          <rect x={x} y="52" width="16" height="80" rx="2.5" fill="url(#valveGrad)" stroke={goldDeep} strokeWidth="0.7" />
          {/* Top cap */}
          <rect x={x - 0.5} y="46" width="17" height="8" rx="2" fill={goldMid} stroke={goldDeep} strokeWidth="0.6" />
          {/* Bottom cap */}
          <rect x={x - 0.5} y="130" width="17" height="8" rx="2" fill={goldMid} stroke={goldDeep} strokeWidth="0.6" />
          {/* Valve stem rising above casing */}
          <rect x={x + 5.5} y="30" width="5" height="18" rx="1.2" fill={goldMid} stroke={goldDeep} strokeWidth="0.5" />
          {/* Pearl valve button (mother-of-pearl inset look) */}
          <circle cx={x + 8} cy="26" r="6" fill={goldDark} stroke={goldDeep} strokeWidth="0.6" />
          <circle cx={x + 8} cy="26" r="4.2" fill={ivoryHi} stroke={goldMid} strokeWidth="0.5" />
          <circle cx={x + 6.5} cy="24.5" r="1.4" fill="#FFFFFF" opacity="0.9" />
        </g>
      ))}

      {/* ── Water-key (spit valve) small lever under first valve */}
      <path d="M 174 132 L 180 148 L 176 149 L 172 133 Z" fill={goldDark} stroke={goldDeep} strokeWidth="0.5" />
      <circle cx="178" cy="148" r="1.6" fill={goldMid} />

      {/* ── Bell tube (curves up and out to the right) */}
      <path
        d="M 228 78
           C 250 66, 268 66, 282 76
           L 282 108
           C 268 118, 250 118, 228 106 Z"
        fill="url(#tubeGrad)"
        stroke={goldDeep}
        strokeWidth="0.8"
      />
      {/* Bell highlight streak */}
      <path d="M 232 75 C 254 66, 268 66, 280 74" stroke={ivoryHi} strokeWidth="1.2" opacity="0.75" fill="none" />

      {/* ── Bell flare */}
      <path
        d="M 278 62
           L 314 40
           Q 318 92, 314 144
           L 278 122
           C 268 108, 268 78, 278 62 Z"
        fill="url(#bellGrad)"
        stroke={goldDeep}
        strokeWidth="0.9"
      />
      {/* Bell inner shadow */}
      <path
        d="M 282 74
           L 306 60
           Q 310 92, 306 124
           L 282 110
           C 274 100, 274 84, 282 74 Z"
        fill={goldDark}
        opacity="0.45"
      />
      {/* Bell rim band (top and bottom of the flare opening) */}
      <path d="M 314 40 Q 318 92, 314 144" stroke="url(#rimGrad)" strokeWidth="4" fill="none" strokeLinecap="round" />
      <ellipse cx="314" cy="92" rx="2.5" ry="52" fill={goldDeep} opacity="0.55" />

      {/* ── Small brace connecting bell tube to leadpipe (structural bar) */}
      <rect x="196" y="82" width="6" height="20" fill={goldMid} stroke={goldDeep} strokeWidth="0.4" opacity="0" />
      <path d="M 122 90 L 168 90" stroke={goldDark} strokeWidth="1.2" opacity="0.4" />

      {/* ── Gold tassel hanging under the bell */}
      <g transform="translate(268,124)">
        {/* Ring/anchor loop */}
        <circle cx="0" cy="0" r="2.4" fill="none" stroke={goldDeep} strokeWidth="1.2" />
        {/* Cord */}
        <line x1="0" y1="1.5" x2="0" y2="13" stroke={goldDeep} strokeWidth="1.8" />
        {/* Tassel knot */}
        <ellipse cx="0" cy="15" rx="5.5" ry="4.5" fill={goldDeep} />
        <ellipse cx="0" cy="15" rx="4.2" ry="3.4" fill="url(#tasselGrad)" />
        <ellipse cx="-1.2" cy="13.8" rx="1.4" ry="0.9" fill={ivoryHi} opacity="0.9" />
        {/* Tassel strands (dark outline for contrast, gold inside) */}
        {[-4.5, -2.5, -0.8, 1, 2.8, 4.5].map((x, i) => {
          const drop = 16 + Math.abs(x) * 0.4;
          return (
            <g key={i}>
              <line x1={x} y1={18} x2={x * 1.15} y2={18 + drop} stroke={goldDeep} strokeWidth="2.2" strokeLinecap="round" />
              <line x1={x} y1={18} x2={x * 1.15} y2={18 + drop} stroke={gold} strokeWidth="1.1" strokeLinecap="round" />
            </g>
          );
        })}
        {/* Tiny highlight bead atop knot */}
        <circle cx="0" cy="11.5" r="0.9" fill={ivoryHi} opacity="0.9" />
      </g>

      {/* ── Subtle overall specular highlight on bell */}
      <ellipse cx="292" cy="72" rx="10" ry="4" fill={ivoryHi} opacity="0.55" />
    </svg>
  );
}

function ConfettiDots({ active, reducedMotion }) {
  const dots = [
    { top: "18%", left: "5%", size: 6, color: "#C6A85A", delay: 0 },
    { top: "72%", left: "8%", size: 4, color: "#E6C871", delay: 100 },
    { top: "28%", left: "42%", size: 5, color: "#C6A85A", delay: 60 },
    { top: "78%", left: "48%", size: 4, color: "#E6C871", delay: 180 },
    { top: "14%", left: "78%", size: 6, color: "#C6A85A", delay: 30 },
    { top: "62%", left: "92%", size: 5, color: "#E6C871", delay: 220 },
    { top: "88%", left: "72%", size: 4, color: "#C6A85A", delay: 140 },
    { top: "8%", left: "62%", size: 2, color: "#E33278", delay: 200 },
    { top: "94%", left: "22%", size: 2, color: "#E33278", delay: 260 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            top: d.top,
            left: d.left,
            width: d.size,
            height: d.size,
            backgroundColor: d.color,
            boxShadow: `0 0 12px ${d.color}`,
            opacity: active ? 1 : 0.55,
            transform: active && !reducedMotion ? "translateY(-6px) scale(1.35)" : "none",
            transition: `all 500ms ease ${d.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}
